/**
 * Колокольчик — последние события для текущего пользователя.
 *
 * Источников ДВА, и это важно: `/api/notifications` (журнал движка: входы,
 * выходы, TP/SL) и `/api/alerts/feed` (сработавшие пользовательские правила).
 * Раньше был только первый, поэтому алерты не показывались в колокольчике
 * вообще — их было видно исключительно на вкладке «Алерты», и то пока она
 * открыта. Живые срабатывания досыпаем по WS, не дожидаясь опроса.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { useEscape } from '../utils/useEscape';
import { exchangeLabel } from './ExchangeIcon';
import { Bell, ExternalLink, Volume2, VolumeX } from 'lucide-react';
import { playSound, setTradeSound, tradeSoundOn } from '../utils/sounds';
import clsx from 'clsx';
import { apiClient } from '../api/client';
import { wsManager } from '../api/websocket';
import { usePolling } from '../utils/poll';
import { sharedGet, NOTIFICATIONS_URL } from '../utils/sharedGet';
import { formatRouteEvent, normalizeRouteEvent } from '../utils/routeView';

interface NotificationItem {
  ts: number;
  route_id: number;
  kind: string;
  accepted?: boolean;
  reason?: string;
  direction?: string;
  data?: Record<string, any>;
}

/** Один-строчное человекочитаемое описание события. Зеркалит формат
 *  Telegram-нотификаций и Infinity Dashboard ленты — чтобы во всех местах
 *  выглядело одинаково. */
function describe(it: NotificationItem): { text: string; tone: 'green'|'red'|'yellow'|'muted' } {
  const d = it.data || {};
  const t = (key: string, opts?: Record<string, unknown>): string =>
    i18n.t(key.includes(':') ? key : `notificationsBell:${key}`, opts as any) as string;
  const fmt$ = (v: any, dp = 2) => {
    const n = Number(v); if (!Number.isFinite(n)) return '$—';
    return `${n < 0 ? '-' : ''}$${Math.abs(n).toFixed(dp)}`;
  };
  const fmtPct = (v: any, dp = 3) => {
    const n = Number(v); if (!Number.isFinite(n)) return '—%';
    return `${n.toFixed(dp)}%`;
  };
  // Социальные события: приглашения в партию, заявки в друзья, результаты.
  if (it.kind?.startsWith('social:')) {
    const k = it.kind.slice(7);
    const who = String(d.from || d.who || '');
    const game = d.game === 'checkers' ? t('game.checkers') : d.game === 'minesweeper' ? t('game.minesweeper') : t('game.chess');
    if (k === 'game_invite')   return { tone: 'yellow', text: t('social.gameInvite', { who, game }) };
    if (k === 'game_accepted') return { tone: 'green',  text: t('social.gameAccepted', { who }) };
    if (k === 'game_finished') return { tone: 'muted',  text: t('social.gameFinished', { who }) };
    if (k === 'friend_request') return { tone: 'yellow', text: t('social.friendRequest', { who }) };
    if (k === 'friend_added')   return { tone: 'green',  text: t('social.friendAdded', { who }) };
    return { tone: 'muted', text: `${who}` };
  }
  // Сработавшее правило: kind приходит как `alert:<тип>`, данные — сам fire.
  if (it.kind?.startsWith('alert:')) {
    const sym = String(d.symbol ?? '');
    const kind = it.kind.slice(6);
    if (kind === 'price') return { tone: 'yellow', text: `🔔 ${sym} @ ${d.exchange}: ${fmt$(d.price, 4)}` };
    const exchange = String(d.exchange);
    if (kind === 'liquidation') return { tone: 'red', text: t('alert.liquidation', { sym, exchange }) };
    if (kind === 'funding_flip') return { tone: 'yellow', text: t('alert.fundingFlip', { sym, exchange }) };
    if (kind === 'funding_soon') return { tone: 'yellow', text: t('alert.fundingSoon', { sym, exchange }) };
    if (kind === 'deviation') return { tone: 'yellow', text: t('alert.deviation', { sym, exchange, pct: fmtPct(d.deviation_pct) }) };
    // Системное ЧП от сканера позиций: у него нет спреда, раньше падало в
    // общую ветку и рисовалось как «🔔 BTCUSDT →: —%».
    if (kind === 'hedge_leg_gone') {
      const side = d.side === 'long' ? t('common:long') : d.side === 'short' ? t('common:short') : t('alert.leg');
      return { tone: 'red',
        text: t('alert.hedgeLegGone', { sym, side,
          exchange: exchangeLabel(String(d.exchange ?? '')),
          other: exchangeLabel(String(d.other_exchange ?? '')) }) };
    }
    if (kind === 'exit_spread') return { tone: 'yellow',
      text: t('alert.exitSpread', { sym,
        longEx: exchangeLabel(String(d.long_exchange ?? '')),
        shortEx: exchangeLabel(String(d.short_exchange ?? '')),
        pct: fmtPct(d.exit_spread_pct) }) };
    return { tone: 'yellow',
      text: `🔔 ${sym} ${d.short_exchange ?? ''}→${d.long_exchange ?? ''}: ${fmtPct(d.spread_pct)}` };
  }
  switch (it.kind) {
    case 'paper_open_filled':
    case 'live_open_filled':
      return { tone: 'green',
        text: t('engine.opened', { usd: fmt$(d.usd, 0), filled: fmt$(d.filled_usd, 0), total: fmt$(d.total_size, 0) })
              + (Number.isFinite(Number(d.spread_open)) ? t('engine.openedSpread', { pct: fmtPct(d.spread_open) }) : '') };
    case 'paper_close_filled':
    case 'live_close_filled': {
      const pnl = Number(d.pnl_usd ?? 0);
      return { tone: pnl >= 0 ? 'green' : 'red',
        text: t('engine.closed', { usd: fmt$(d.usd, 0), pnl: `${pnl >= 0 ? '+' : ''}${fmt$(pnl)}` }) };
    }
    case 'take_profit_triggered':
      return { tone: 'green', text: t('engine.takeProfit') };
    case 'stop_loss_triggered':
      return { tone: 'red',   text: t('engine.stopLoss') };
    case 'live_open_failed':
    case 'live_close_failed':
      return { tone: 'red',   text: t('engine.rejected', { reason: it.reason || '—' }) };
    case 'live_open_one_sided':
    case 'live_close_one_sided':
      return { tone: 'red',   text: t('engine.oneSided') };
    default: {
      // Остальные события сессии (сверка позиции, плечо, проскальзывание…) —
      // тем же текстом, что лента карточки автоарбитража. Раньше здесь
      // показывался служебный код вроде «live_position_resync».
      const f = formatRouteEvent(normalizeRouteEvent(it));
      return { tone: f.tone, text: f.text };
    }
  }
}

const TONE_CLS: Record<'green'|'red'|'yellow'|'muted', string> = {
  green:  'text-trade-green',
  red:    'text-trade-red',
  yellow: 'text-primary',
  muted:  'text-muted-foreground',
};

/** Ссылка на терминал по событию: символ + ноги, если они известны.
 *  Для алерта на одной бирже (цена, ликвидация) вторую ногу не выдумываем —
 *  терминал сам подберёт, если её нет в адресе. */
function terminalLink(it: NotificationItem): string | null {
  const d = it.data || {};
  // Приглашение в партию ведёт на доску, а не в терминал.
  if (it.kind?.startsWith('social:')) {
    if (d.game_id) return `/${d.game === 'checkers' || d.game === 'minesweeper' ? d.game : 'chess'}?game=${d.game_id}`;
    return '/games';
  }
  const sym = String(d.symbol || '');
  if (!sym) return null;
  const p = new URLSearchParams({ symbol: sym });
  const long = d.long_exchange || d.exchange;
  const short = d.short_exchange;
  if (long) p.set('long', String(long));
  if (short) p.set('short', String(short));
  return `/?${p.toString()}`;
}

/** Одно и то же срабатывание приходит дважды: сразу по WS и через пять секунд
 *  из `/api/alerts/feed`. В списке оно двоилось, поэтому склеиваем по времени и
 *  содержимому — id у события нет. */
function dedupe(items: NotificationItem[]): NotificationItem[] {
  const seen = new Set<string>();
  const out: NotificationItem[] = [];
  for (const it of items.sort((x, y) => (y.ts || 0) - (x.ts || 0))) {
    const d = it.data || {};
    const key = [it.ts, it.kind, it.route_id, d.symbol, d.exchange,
                 d.long_exchange, d.short_exchange].join('|');
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(it);
  }
  return out.slice(0, 30);
}

export const NotificationsBell: React.FC = () => {
  const { t } = useTranslation('notificationsBell');
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [open, setOpen] = useState(false);
  const [lastSeenTs, setLastSeenTs] = useState<number>(() => {
    try { return Number(localStorage.getItem('sb_notif_last_seen') || 0); } catch { return 0; }
  });
  const ref = useRef<HTMLDivElement | null>(null);
  // Звук алертов. Ключ общий с AlertNotifications — там он и проигрывается.
  const [sound, setSound] = useState<boolean>(() => {
    try { return localStorage.getItem('sb_alert_sound') !== '0'; } catch { return true; }
  });
  // Звук заливки и выливки — отдельный выключатель (играет TradeSounds).
  const [tradeSound, setTradeSoundState] = useState<boolean>(tradeSoundOn);

  // Три ручки за один заход. Опрос — через usePolling: он не пускает новый
  // запрос, пока в полёте предыдущий, и молчит на скрытой вкладке. Раньше тут
  // стоял голый setInterval на 5 секунд, и на медленном ответе запросы
  // наслаивались: за первые 20 секунд загрузки терминала набегало под 60
  // обращений к API, в очереди к однопоточному бэкенду история спреда ждала
  // 15.8 секунды, и график выглядел зависшим.
  usePolling(async () => {
    try {
      const [j, a, soc] = await Promise.all([
        sharedGet(NOTIFICATIONS_URL, 10_000).then(data => ({ data })),
        apiClient.raw.get('/api/alerts/feed?limit=20').catch(() => ({ data: { items: [] } })),
        // Приглашения в партию и заявки в друзья: человек должен увидеть их
        // там же, где всё остальное, а не случайно зайдя на страницу игр.
        apiClient.raw.get('/api/social/events').catch(() => ({ data: { events: [] } })),
      ]);
      const fires: NotificationItem[] = (a.data?.items || []).map((f: any) => ({
        ts: Number(f.ts) || 0, route_id: 0, kind: `alert:${f.kind || 'spread'}`, data: f,
      }));
      const socItems: NotificationItem[] = (soc.data?.events || []).map((e: any) => ({
        ts: Number(e.ts) || 0, route_id: 0, kind: `social:${e.kind}`, data: e,
      }));
      setItems(dedupe([...(j.data?.items || []), ...fires, ...socItems]));
    } catch {/* not logged in / backend off — silently */}
    // 15 с вместо 5: срочное и так приходит сокетом (подписка ниже), опрос тут
    // нужен лишь чтобы подобрать пропущенное и то, что сокет не шлёт вовсе —
    // приглашения в партию и заявки в друзья.
  }, [], { intervalMs: 15000 });

  useEffect(() => {
    // Живое срабатывание — сразу в список, чтобы значок реагировал мгновенно,
    // а не через пятнадцать секунд.
    wsManager.subscribeChannels(['alerts']);
    const unsub = wsManager.subscribe('alert', (msg: any) => {
      const f = msg?.data;
      if (!f?.ts) return;
      setItems(prev => dedupe([{ ts: Number(f.ts), route_id: 0,
                                 kind: `alert:${f.kind || 'spread'}`, data: f }, ...prev]));
    });
    return () => { unsub?.(); };
  }, []);

  // close on outside click
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);
  // 29.09: и по Esc — как остальные выпадашки (общий стек слоёв).
  useEscape(open, () => setOpen(false));

  const unread = items.filter(i => (i.ts || 0) > lastSeenTs).length;

  const openPanel = () => {
    setOpen(true);
    // Используем Date.now(), а не max(items.ts) — иначе engine при активной
    // торговле успевает сгенерировать новые события между poll-ами, и счётчик
    // мгновенно прыгает обратно. Now() ставит водяной знак на момент клика,
    // и новые события засчитываются как unread только если пришли ПОСЛЕ.
    const now = Date.now();
    setLastSeenTs(now);
    try { localStorage.setItem('sb_notif_last_seen', String(now)); } catch {}
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => open ? setOpen(false) : openPanel()}
        aria-label={t('ui.title')}
        className={clsx('sb-iconbtn sb-bell relative', open && '!bg-primary/10 !text-foreground ring-1 ring-primary/40')}
      >
        <Bell className={clsx('h-[18px] w-[18px]', unread > 0 && 'sb-ring')} />
        {unread > 0 && (
          <span className="sb-badge absolute -right-1 -top-1 inline-flex min-w-[18px] h-[18px] items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        // На мобиле панель растягивается на всю ширину viewport'а с небольшими
        // полями (calc), на десктопе фиксированные 360px справа. max-h тоже
        // адаптивный — на мобиле учитываем размер экрана через svh-единицы.
        // flex-col + список flex-1 min-h-0: у списка был свой max-h на всю
        // высоту панели, а над ним три строки шапки — нижние события
        // обрезались и не прокручивались.
        <div className="fixed sm:absolute inset-x-2 sm:inset-x-auto sm:right-0 top-14 sm:top-full sm:mt-2 sm:w-[360px] max-h-[70svh] sm:max-h-[420px] flex flex-col overflow-hidden rounded-md border border-border bg-popover shadow-xl z-50">
          <div className="flex items-center justify-between border-b border-border px-3 py-2">
            <span className="text-xs font-semibold">{t('ui.title')}</span>
            <span className="text-[12px] text-muted-foreground">{t('ui.events', { count: items.length })}</span>
          </div>
          <div className="flex items-center justify-between border-b border-border px-3 py-1.5">
            <span className="text-[13px] text-muted-foreground">{t('ui.alertSound')}</span>
            <button onClick={() => { const v = !sound; setSound(v);
                     try { localStorage.setItem('sb_alert_sound', v ? '1' : '0'); } catch {} }}
              title={sound ? t('ui.soundOff') : t('ui.soundOn')}
              className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[13px] text-muted-foreground hover:bg-accent hover:text-foreground">
              {sound ? <Volume2 size={12} /> : <VolumeX size={12} />}
              {sound ? t('ui.hasSound') : t('ui.noSound')}
            </button>
          </div>
          <div className="flex items-center justify-between border-b border-border px-3 py-1.5">
            <span className="text-[13px] text-muted-foreground">{t('ui.tradeSound')}</span>
            <button onClick={() => { const v = !tradeSound; setTradeSoundState(v); setTradeSound(v);
                     if (v) playSound('enter', true); }}
              title={t('ui.tradeSoundHint')}
              className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[13px] text-muted-foreground hover:bg-accent hover:text-foreground">
              {tradeSound ? <Volume2 size={12} /> : <VolumeX size={12} />}
              {tradeSound ? t('ui.hasSound') : t('ui.noSound')}
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {items.length === 0 && (
              <div className="px-4 py-8 text-center text-xs text-muted-foreground">
                {t('ui.empty')}
              </div>
            )}
            {items.map((it, i) => {
              const d = describe(it);
              const href = terminalLink(it);
              return (
                <div key={i} className="border-b border-border/60 px-3 py-2 hover:bg-accent/30 transition-colors">
                  <div className="flex items-center justify-between gap-2 text-[12px]">
                    {/* У алерта нет маршрута — «#0» здесь было мусором. */}
                    <span className="text-muted-foreground font-mono">
                      {it.route_id ? `#${it.route_id}` : t('ui.alert')}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <span className="text-muted-foreground font-mono">
                        {it.ts ? new Date(it.ts).toLocaleTimeString('ru-RU') : '—'}
                      </span>
                      {href && (
                        <a href={href} target="_blank" rel="noopener noreferrer"
                           title={t('ui.openInTerminal')}
                           className="text-muted-foreground hover:text-primary">
                          <ExternalLink size={11} />
                        </a>
                      )}
                    </span>
                  </div>
                  <div className={clsx('mt-0.5 text-[12px] leading-tight', TONE_CLS[d.tone])}>
                    {d.text}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsBell;
